import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Keeps the main video slideshow and thumbnail slideshow in sync.
 *
 * @extends {Component}
 */
class ProductVideoReviews extends Component {
  requiredRefs = ['mainSlideshow', 'thumbsSlideshow'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('click', this.#handleThumbSlideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.removeEventListener('click', this.#handleThumbSlideClick);
  }

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const detail = event.detail;
    if (!detail?.userInitiated) return;

    const main = /** @type {import('./slideshow.js').Slideshow | undefined} */ (this.refs.mainSlideshow);
    const thumbs = /** @type {import('./slideshow.js').Slideshow | undefined} */ (this.refs.thumbsSlideshow);

    if (!main || !thumbs) return;

    const { index, trigger } = detail;

    if (event.target === main) {
      if (thumbs.current === index) return;
      thumbs.select(index, undefined, { animate: true });
      return;
    }

    if (event.target !== thumbs) return;

    // Multi-slide thumb strip: scroll + drag "current" index comes from geometry, not the active review.
    // After programmatic thumbs.select (sync from main), spurious drag events often fire with a wrong index
    // and yank main back. Real selection uses #handleThumbSlideClick; thumb arrows use trigger "select".
    if (trigger === 'scroll' || trigger === 'drag') return;

    if (main.current === index) return;
    main.select(index, undefined, { animate: true });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleThumbSlideClick = (event) => {
    const thumbs = /** @type {import('./slideshow.js').Slideshow | undefined} */ (this.refs.thumbsSlideshow);
    const main = /** @type {import('./slideshow.js').Slideshow | undefined} */ (this.refs.mainSlideshow);

    if (!thumbs || !main || !(event.target instanceof Element)) return;

    const slide = event.target.closest('slideshow-slide');
    if (!(slide instanceof HTMLElement) || !thumbs.contains(slide)) return;

    const { slides } = thumbs;
    if (!slides?.length) return;

    const index = slides.indexOf(slide);
    if (index < 0) return;
    if (main.current === index) return;

    main.select(index, event, { animate: true });
  };
}

if (!customElements.get('product-video-reviews')) {
  customElements.define('product-video-reviews', ProductVideoReviews);
}
